import { LogicExpressionJson, LogicType } from '@doss/common-all'
import { Box, Theme, Typography } from '@mui/material'
import { Children, useMemo } from 'react'
import useStyles from '~/common/hooks/useStyles'
import { gray } from '~/common/styles/colors'
import { FormulaKeyword } from '../formula/FormulaKeyword'
import { ConditionExpressionValue } from './ConditionExpressionValue'

interface ConditionExpressionRendererProps {
  data: LogicExpressionJson
}

function getStyles(theme: Theme) {
  return {
    container: {
      display: 'flex',
      gap: '4px',
      alignItems: 'center',
      whiteSpace: 'nowrap',
    },
    chip: {
      border: `1px solid ${gray[4]}`,
      borderRadius: '2px',
      padding: theme.spacing(0.5),
      backgroundColor: gray[1],
      height: 'auto',
      whiteSpace: 'nowrap',
      display: 'inline-flex', // Use inline-flex to ensure the background covers all content
      minWidth: 'min-content', // Ensure the chip is at least as wide as its content
    },
  }
}

/**
 * Renders a logic condition expression
 * @param data
 * @constructor
 */
export function ConditionExpressionRenderer({ data }: ConditionExpressionRendererProps) {
  const styles = useStyles(getStyles)

  const content = useMemo(() => {
    switch (data.type) {
      case LogicType.BOOLEAN: {
        return <FormulaKeyword>{data.value.toString()}</FormulaKeyword>
      }
      case LogicType.AND:
      case LogicType.OR: {
        return (
          <Box sx={styles.container}>
            <Typography noWrap>
              <FormulaKeyword>{data.type}</FormulaKeyword>(
            </Typography>
            {Children.toArray(
              data.expressions.map((expression, idx) => (
                <>
                  {idx !== 0 && <Typography>, </Typography>}
                  <ConditionExpressionRenderer data={expression} />
                </>
              )),
            )}
            <Typography>)</Typography>
          </Box>
        )
      }
      case LogicType.NOT: {
        return (
          <Box sx={styles.container}>
            <Typography>
              <FormulaKeyword>NOT</FormulaKeyword>(
            </Typography>
            <ConditionExpressionRenderer data={data.expression} />
            <Typography>)</Typography>
          </Box>
        )
      }
      case LogicType.COMPARISON: {
        return (
          <Box sx={styles.container}>
            <ConditionExpressionValue value={data.left} />
            <Typography noWrap>
              <FormulaKeyword>{data.condition}</FormulaKeyword>
            </Typography>
            <ConditionExpressionValue value={data.right} />
          </Box>
        )
      }
      case LogicType.UNARY: {
        return (
          <Box sx={styles.container}>
            <ConditionExpressionValue value={data.value} />
            <Typography>
              <FormulaKeyword>{data.condition}</FormulaKeyword>
            </Typography>
          </Box>
        )
      }
    }
  }, [data, styles.container])

  return <Box sx={styles.chip}>{content}</Box>
}
